import axiosInstance from "@app/axiosInstance";
import {
  T_Member,
  T_MemberAvailability,
  T_MemberStatus,
} from "@app/entities/member/types";
import { useCompany } from "@app/providers/CompanyContextProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import notification from "antd/lib/notification";

export const useMemberMutations = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.companyId;

  const { mutate: updateMember, isPending: isUpdateMemberPending } =
    useMutation({
      mutationFn: ({ entityId, member }: { entityId: number; member: T_Member }) =>
        axiosInstance.put(`/admin/${companyId}/member/${entityId}`, member),
      onError: (error) => {
        console.error(error);
        notification.error({ title: "Failed to update member" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["members"] });
        notification.success({
          description: "Member has been updated",
          title: "Success",
        });
      },
    });

  const { mutate: deleteMember, isPending: isDeleteMemberPending } =
    useMutation({
      mutationFn: ({ entityId }: { entityId: number }) =>
        axiosInstance.delete(`/admin/${companyId}/member/${entityId}`),
      onError: (error) => {
        console.error(error);
        notification.error({ title: "Failed to delete member" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["members"] });
        notification.success({
          description: "Member has been deleted",
          title: "Success",
        });
      },
    });

  const {
    mutate: updateMemberAvailability,
    isPending: isUpdateMemberAvailabilityPending,
  } = useMutation({
    mutationFn: ({
      entityId,
      availability,
    }: {
      entityId: number;
      availability: Partial<T_MemberAvailability>;
    }) =>
      axiosInstance.put(
        `/admin/${companyId}/member/${entityId}/availability`,
        availability,
      ),
    onError: (error) => {
      console.error(error);
      notification.error({ title: "Failed to update member availability" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      notification.success({
        description: "Member availability has been updated",
        title: "Success",
      });
    },
  });

  const { mutate: updateMemberStatus, isPending: isUpdateMemberStatusPending } =
    useMutation({
      mutationFn: ({
        entityId,
        status,
      }: {
        entityId: number;
        status: T_MemberStatus;
      }) =>
        axiosInstance.put(`/admin/${companyId}/member/${entityId}/company`, {
          status,
        }),
      onError: (error) => {
        console.error(error);
        notification.error({ title: "Failed to update member status" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["members"] });
        notification.success({
          description: "Member status has been updated",
          title: "Success",
        });
      },
    });

  const isMutationPending =
    isUpdateMemberPending ||
    isUpdateMemberAvailabilityPending ||
    isUpdateMemberStatusPending ||
    isDeleteMemberPending;
  return {
    isMutationPending,
    deleteMember,
    updateMember,
    updateMemberAvailability,
    updateMemberStatus,
  };
};
